/**
 * Profile Page
 *
 * Unified profile page with tabbed navigation for Activity, My Gyms, Admin (if admin), and Settings.
 * Uses URL-based tab state for shareable links and browser history support.
 */

import { useState, useEffect, useMemo, useRef, type CSSProperties } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useGymMemberships } from '@/hooks/useGymMemberships';
import { useBreadcrumbs } from '@/contexts/BreadcrumbContext';
import { useUpdateDisplayName } from '@/hooks/useUpdateDisplayName';
import { ProfileHeader } from '@/components/profile/ProfileHeader';
import { EditDisplayNameSheet } from '@/components/profile/EditDisplayNameSheet';
import { ActivityTab } from '@/components/profile/ActivityTab';
import { type ActivityView } from '@/components/profile/ActivitySubTabs';
import { MyGymsTab } from '@/components/profile/MyGymsTab';
import { AdminTab } from '@/components/profile/AdminTab';
import { SettingsTab } from '@/components/profile/SettingsTab';
import { PageLoader } from '@/components/ui/PageLoader';
import { SpotlightOverlay, useOnboarding, type SpotlightStep } from '@/components/onboarding';

type TabId = 'activity' | 'mygyms' | 'admin' | 'settings';

/**
 * ProfilePage displays user profile with tabbed navigation
 */
export function ProfilePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAdmin, loading: authLoading } = useAuth();
  const { memberships, loading: membershipsLoading } = useGymMemberships();
  const { setBreadcrumbs } = useBreadcrumbs();
  const { updateDisplayName } = useUpdateDisplayName();

  const [showEditName, setShowEditName] = useState(false);

  // Onboarding state
  const { isOnboarding, currentPhase, currentStep, nextStep, nextPhase, completeOnboarding } =
    useOnboarding();
  const showProfileOnboarding = isOnboarding && currentPhase === 'profile';

  // Handle transition from climb phase to profile phase
  // When user navigates to Profile during climb phase step 8, transition to profile phase
  // Use a ref to ensure we only transition once (React Strict Mode runs effects twice)
  const hasTransitionedRef = useRef(false);
  useEffect(() => {
    if (
      isOnboarding &&
      currentPhase === 'climb' &&
      currentStep === 8 &&
      !hasTransitionedRef.current
    ) {
      hasTransitionedRef.current = true;
      nextPhase();
    }
    // Reset the ref when not in climb phase step 8
    if (currentPhase !== 'climb' || currentStep !== 8) {
      hasTransitionedRef.current = false;
    }
  }, [isOnboarding, currentPhase, currentStep, nextPhase]);

  // Onboarding steps for the Profile page (4 steps)
  const profileOnboardingSteps: SpotlightStep[] = useMemo(
    () => [
      {
        id: 'activity',
        targetSelector: 'profile-tab-activity',
        title: 'Your Activity',
        description: 'See your climb history here—sends, attempts, and stats over time.',
        tooltipPosition: 'bottom',
        padding: 8,
      },
      {
        id: 'mygyms',
        targetSelector: 'profile-tab-mygyms',
        title: 'My Gyms',
        description: 'View gyms you follow and manage your home gym.',
        tooltipPosition: 'bottom',
        padding: 8,
      },
      {
        id: 'settings',
        targetSelector: 'profile-tab-settings',
        title: 'Settings',
        description: 'Customize your experience—dark mode, account settings, and more.',
        tooltipPosition: 'bottom',
        padding: 8,
      },
      {
        id: 'complete',
        targetSelector: 'onboarding-complete', // Non-existent, centers tooltip
        title: "You're All Set!",
        description: "You're ready to start climbing. Have fun and send hard!",
        tooltipPosition: 'bottom',
        hideActionButton: true,
        skipButtonLabel: 'Start Climbing!',
      },
    ],
    []
  );

  // Get current tab from URL or default to 'activity'
  const VALID_TABS: TabId[] = ['activity', 'mygyms', 'admin', 'settings'];
  const tabParam = searchParams.get('tab');
  const currentTab: TabId = VALID_TABS.includes(tabParam as TabId)
    ? (tabParam as TabId)
    : 'activity';

  // Get current activity view from URL or default to 'history'
  const VALID_VIEWS: ActivityView[] = ['history', 'statistics'];
  const viewParam = searchParams.get('view');
  const currentView: ActivityView = VALID_VIEWS.includes(viewParam as ActivityView)
    ? (viewParam as ActivityView)
    : 'history';

  useEffect(() => {
    setBreadcrumbs([{ label: 'Flash', path: '/' }, { label: 'Profile' }]);
  }, [setBreadcrumbs]);

  if (authLoading || membershipsLoading) {
    return <PageLoader />;
  }

  if (!user) {
    return null;
  }

  // Determine user's gym associations from memberships
  const ownedGymIds = memberships.filter((m) => m.role === 'owner').map((m) => m.gymId);
  const setterGymIds = memberships.filter((m) => m.role === 'setter').map((m) => m.gymId);
  const userRole =
    ownedGymIds.length > 0 ? 'owner' : setterGymIds.length > 0 ? 'setter' : 'climber';

  // Available tabs based on role
  const tabs: { id: TabId; label: string }[] = [
    { id: 'activity', label: 'Activity' },
    { id: 'mygyms', label: 'My Gyms' },
    ...(isAdmin ? [{ id: 'admin' as TabId, label: 'Admin' }] : []),
    { id: 'settings', label: 'Settings' },
  ];

  const handleTabChange = (tabId: TabId) => {
    setSearchParams({ tab: tabId });
  };

  const handleActivityViewChange = (view: ActivityView) => {
    setSearchParams({ tab: 'activity', view });
  };

  const handleSaveDisplayName = async (newName: string) => {
    await updateDisplayName(newName);
  };

  return (
    <div style={containerStyle}>
      {/* Profile Header */}
      <ProfileHeader
        displayName={user.displayName || 'Anonymous'}
        email={user.email || ''}
        photoURL={user.photoURL}
        onEditName={() => setShowEditName(true)}
      />

      {/* Tab Navigation */}
      <div style={tabBarStyle}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            id={`profile-tab-${tab.id}`}
            style={{
              ...tabStyle,
              ...(currentTab === tab.id ? tabActiveStyle : {}),
            }}
            onClick={() => handleTabChange(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={tabContentStyle}>
        {currentTab === 'activity' && (
          <ActivityTab initialView={currentView} onViewChange={handleActivityViewChange} />
        )}
        {currentTab === 'mygyms' && (
          <MyGymsTab userRole={userRole} ownedGymIds={ownedGymIds} setterGymIds={setterGymIds} />
        )}
        {currentTab === 'admin' && isAdmin && (
          <AdminTab pendingGymRequests={0} pendingFeedback={0} />
        )}
        {currentTab === 'settings' && <SettingsTab />}
      </div>

      {/* Edit Display Name Sheet */}
      <EditDisplayNameSheet
        isOpen={showEditName}
        currentName={user.displayName || ''}
        onClose={() => setShowEditName(false)}
        onSave={handleSaveDisplayName}
      />

      {/* Spotlight Onboarding for first-time users */}
      <SpotlightOverlay
        steps={profileOnboardingSteps}
        currentStep={currentStep}
        isVisible={showProfileOnboarding}
        onNext={nextStep}
        onSkip={completeOnboarding}
        onComplete={completeOnboarding}
      />
    </div>
  );
}

/**
 * Component styles using CSS variables for theming
 */
const containerStyle: CSSProperties = {
  height: '100%',
  overflow: 'auto',
  backgroundColor: 'var(--color-bg-primary)',
};

const tabBarStyle: CSSProperties = {
  display: 'flex',
  borderBottom: '1px solid var(--color-border)',
  backgroundColor: 'var(--color-bg-primary)',
  position: 'sticky',
  top: 0,
  zIndex: 10,
};

const tabStyle: CSSProperties = {
  flex: 1,
  padding: 'var(--spacing-md)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 500,
  color: 'var(--color-text-secondary)',
  backgroundColor: 'transparent',
  borderTop: 'none',
  borderLeft: 'none',
  borderRight: 'none',
  borderBottomWidth: '2px',
  borderBottomStyle: 'solid',
  borderBottomColor: 'transparent',
  cursor: 'pointer',
};

const tabActiveStyle: CSSProperties = {
  color: 'var(--color-primary)',
  borderBottomColor: 'var(--color-primary)',
};

const tabContentStyle: CSSProperties = {
  paddingBottom: 16, // Minimal padding, tab components handle their own spacing
};

export default ProfilePage;
